import { LocalDate } from '@js-joda/core';

import { AppointmentStatus } from '@dreryk/egabinet-appointment-frontend-api';
import { VisitStatus } from '@dreryk/egabinet-visit-frontend-api';

import { assertUnreachable, convertDateToLocalDate } from '@dreryk/react-common';
import { ContextDialog } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { AppointmentVisitContinueVisitButton } from './AppointmentDetailsContinueVisitButton';
import { AppointmentDetailsShowVisitButton } from './AppointmentDetailsShowVisitButton';
import { AppointmentVisitStartVisitButton } from './AppointmentDetailsStartVisitButton';
import { AppointmentDetailsTermUpdateLink } from './AppointmentDetailsTermUpdateLink';

type AppointmentDetailsConfirmButtonProps = {
   readonly patientId: number;
   readonly appointmentId: number;
   readonly beginDate: Date;
   readonly visitId: number | undefined;
   readonly onVisitAlreadyStartedFault: VoidFunction;
   readonly onAppointmentAlreadyCancelledFault: VoidFunction;
   readonly appointmentStatus: AppointmentStatus;
   readonly visitStatus: VisitStatus | undefined;
   readonly ownedByCurrentUser: boolean;
   readonly onAppointmentUpdated: VoidFunction;
   readonly timeSlotConfirmed: boolean;
   readonly sourceLabel?: string;
};

export const AppointmentDetailsConfirmButton = (props: AppointmentDetailsConfirmButtonProps): JSX.Element | null => {
   const visitDateTime = convertDateToLocalDate(props.beginDate).atStartOfDay();
   const today = LocalDate.now().atStartOfDay();
   const isFutureVisit = visitDateTime.isAfter(today);

   if (!props.visitStatus) {
      switch (props.appointmentStatus) {
         case 'REGISTERED':
            return (
               <ContextDialog.FooterElement>
                  <>
                     <AppointmentDetailsTermUpdateLink
                        appointmentId={props.appointmentId}
                        onAppointmentUpdated={props.onAppointmentUpdated}
                        timeSlotConfirmed={props.timeSlotConfirmed}
                        sourceLabel={props.sourceLabel}
                     />
                     {protectFeature(
                        'searchingVisitOperations',
                        <AppointmentVisitStartVisitButton
                           appointmentId={props.appointmentId}
                           disabled={isFutureVisit || !props.ownedByCurrentUser}
                           patientId={props.patientId}
                           onVisitAlreadyStartedFault={props.onVisitAlreadyStartedFault}
                           onAppointmentAlreadyCancelledFault={props.onAppointmentAlreadyCancelledFault}
                        />,
                        null
                     )}
                  </>
               </ContextDialog.FooterElement>
            );
         case 'CANCELLED':
         case 'COMPLETED':
            return null;
         default:
            return assertUnreachable(props.appointmentStatus);
      }
   }

   switch (props.visitStatus) {
      case 'IN_PROGRESS':
      case 'FINISHED_NOT_APPROVED':
         return protectFeature(
            'searchingVisitOperations',
            <ContextDialog.FooterElement>
               <AppointmentVisitContinueVisitButton
                  appointmentId={props.appointmentId}
                  visitId={props.visitId!}
                  patientId={props.patientId}
                  disabled={!props.ownedByCurrentUser}
               />
            </ContextDialog.FooterElement>,
            null
         );
      case 'FINISHED_APPROVED':
         return protectFeature(
            'searchingMedicalData',
            <AppointmentDetailsShowVisitButton visitId={props.visitId!} patientId={props.patientId} />,
            null
         );
      case 'CANCELLED':
         return null;
      default:
         return assertUnreachable(props.visitStatus);
   }
};
